import { useEffect, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { detectPhone, type PhoneDetection } from './phoneLayout';
import { BUILD_ID, EditorErrorBoundary } from './EditorErrorBoundary';
import { debugNotesSnapshot } from './debugNotes';

declare const __APP_VERSION__: string;

/**
 * On-screen media diagnostics for the lesson editor, for reading on a phone.
 *
 * Off unless the page URL carries `editorDebug=1` (for example
 * /orders?open=<lessonId>&editorDebug=1). Shows each video's readyState,
 * paused, currentTime, the frames it has actually presented, and its last
 * ten media events. "Frames" is the tell for a frozen picture: currentTime
 * advancing while frames stands still means the element is playing and not
 * painting. Portalled to the body and pointer-events: none, so it cannot
 * change the layout it is watching or take a touch.
 */
function isEditorDebug(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('editorDebug') === '1';
}

const EVENTS = [
  'loadstart', 'loadedmetadata', 'loadeddata', 'canplay', 'canplaythrough',
  'play', 'playing', 'pause', 'seeking', 'seeked', 'waiting', 'stalled',
  'suspend', 'ended', 'emptied', 'error', 'ratechange',
] as const;

interface Watched {
  label: string;
  ref: RefObject<HTMLVideoElement | null>;
}

interface Snapshot {
  label: string;
  readyState: number;
  paused: boolean;
  currentTime: number;
  frames: number | null;
  events: string[];
}

type FrameCallbackVideo = HTMLVideoElement & {
  requestVideoFrameCallback?: (cb: (now: number, meta: { presentedFrames: number }) => void) => number;
  cancelVideoFrameCallback?: (id: number) => void;
};

/**
 * `corner`: the editor's overlay sits top-left, the recorder's bottom-left, so
 * both can be read at once. `detection`: show the phone decision, the four
 * inputs to it, the live viewport and the running build, which is what says
 * whether a phone is running a stale bundle from the service worker.
 */
type OverlayProps = { videos: Watched[]; corner?: 'top' | 'bottom'; detection?: boolean };

/** The overlay in its own error boundary, so a fault in it never takes the editor down. */
export function EditorDebugOverlay(props: OverlayProps) {
  return (
    <EditorErrorBoundary variant="overlay">
      <EditorDebugOverlayInner {...props} />
    </EditorErrorBoundary>
  );
}

function EditorDebugOverlayInner({ videos, corner = 'top', detection = false }: OverlayProps) {
  const [enabled] = useState(isEditorDebug);
  const [rows, setRows] = useState<Snapshot[]>([]);
  const [notes, setNotes] = useState<string[]>([]);
  const [env, setEnv] = useState<{ d: PhoneDetection; inner: string; visual: string; attr: boolean; orientation: string } | null>(null);
  /** Per label: the element listened to, its event log and presented-frame count. */
  const stateRef = useRef(new Map<string, { el: HTMLVideoElement | null; events: string[]; frames: number | null; detach: () => void }>());
  const videosRef = useRef(videos);
  useEffect(() => { videosRef.current = videos; });

  useEffect(() => {
    if (!enabled) return;
    const state = stateRef.current;
    const t0 = performance.now();
    const attach = (label: string, el: HTMLVideoElement) => {
      const entry = { el, events: [] as string[], frames: null as number | null, detach: () => {} };
      const log = (e: Event) => {
        const v = e.target as HTMLVideoElement;
        entry.events = [...entry.events.slice(-9), `${((performance.now() - t0) / 1000).toFixed(1)}s ${e.type} t=${v.currentTime.toFixed(2)} rs=${v.readyState}`];
      };
      EVENTS.forEach((type) => el.addEventListener(type, log));
      const fv = el as FrameCallbackVideo;
      let frameId: number | null = null;
      if (fv.requestVideoFrameCallback) {
        const onFrame = (_now: number, meta: { presentedFrames: number }) => {
          entry.frames = meta.presentedFrames;
          frameId = fv.requestVideoFrameCallback!(onFrame);
        };
        frameId = fv.requestVideoFrameCallback(onFrame);
      }
      entry.detach = () => {
        EVENTS.forEach((type) => el.removeEventListener(type, log));
        if (frameId !== null) fv.cancelVideoFrameCallback?.(frameId);
      };
      state.set(label, entry);
    };
    const timer = setInterval(() => {
      const next: Snapshot[] = [];
      for (const { label, ref } of videosRef.current) {
        const el = ref.current;
        const cur = state.get(label);
        if (cur && cur.el !== el) { cur.detach(); state.delete(label); }
        if (el && !state.has(label)) attach(label, el);
        const entry = state.get(label);
        if (!el || !entry) continue;
        next.push({ label, readyState: el.readyState, paused: el.paused, currentTime: el.currentTime, frames: entry.frames, events: entry.events });
      }
      setRows(next);
      setNotes(debugNotesSnapshot());
      if (detection) {
        const vv = window.visualViewport;
        setEnv({
          d: detectPhone(),
          inner: `${window.innerWidth}x${window.innerHeight}`,
          visual: vv ? `${Math.round(vv.width)}x${Math.round(vv.height)}` : 'n/a',
          attr: document.documentElement.hasAttribute('data-editor-phone'),
          orientation: window.innerWidth > window.innerHeight ? 'landscape' : 'portrait',
        });
      }
    }, 250);
    return () => {
      clearInterval(timer);
      state.forEach((entry) => entry.detach());
      state.clear();
    };
  }, [enabled, detection]);

  if (!enabled) return null;
  return createPortal(
    <div
      className={`fixed left-1 ${corner === 'top' ? 'top-1' : 'bottom-1'} max-w-[95vw] rounded-md p-2 font-mono text-[10px] leading-[13px] text-white pointer-events-none`}
      style={{ zIndex: 2147483647, background: 'rgba(0,0,0,0.78)' }}
      data-testid="editor-debug-overlay"
    >
      {detection && env && (
        <div className="mb-1">
          <div><b>build</b> {typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '?'}</div>
          <div><b>phone</b> {String(env.d.phone)} ({env.d.reason}) attr={String(env.attr)} {env.orientation}</div>
          <div>coarse+noHover={String(env.d.coarseNoHover)} safariTouch={String(env.d.webkitTouch)} minSide={env.d.minSide} touch={String(env.d.touchCapable)} iPhoneUA={String(env.d.iPhoneUA)} iPad={String(env.d.iPad)}</div>
          <div>inner {env.inner} visual {env.visual} touchPoints={navigator.maxTouchPoints}</div>
          <div className="opacity-80">{navigator.userAgent.slice(0, 90)}</div>
        </div>
      )}
      {rows.length === 0 && <div>editorDebug: no video yet</div>}
      {rows.map((r) => (
        <div key={r.label} className="mb-1">
          <div>
            <b>{r.label}</b> rs={r.readyState} {r.paused ? 'paused' : 'playing'} t={r.currentTime.toFixed(2)} frames={r.frames ?? 'n/a'}
          </div>
          {r.events.map((e, i) => <div key={i} className="opacity-80">{e}</div>)}
        </div>
      ))}
      {notes.map((n, i) => <div key={`n${i}`} className="text-amber-300">{n}</div>)}
      {/* Which bundle this is, in every overlay, so a stale one is plain. */}
      <div className="text-white/50">build {BUILD_ID}</div>
    </div>,
    document.body,
  );
}
